import { useState, useRef, useEffect } from 'react';
import { UserProgress } from '../services/storage';
import { aiConversationService, AIConversationContext } from '../services/ai-conversation';
import { ttsService } from '../services/tts';
import { sttService } from '../services/stt';

interface AIConversationPageProps {
  user: UserProgress;
}

export default function AIConversationPage({ user }: AIConversationPageProps) {
  const [messages, setMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [showApiKeyInput, setShowApiKeyInput] = useState(false); // 默认直接开始聊天
  const [selectedLevel, setSelectedLevel] = useState<'beginner' | 'intermediate' | 'advanced'>('beginner');
  const [selectedTopic, setSelectedTopic] = useState<string>('');
  const [customEndpoint, setCustomEndpoint] = useState('');
  const [customModelName, setCustomModelName] = useState('DeepSeek-V4-Pro');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Initialize on first load
  useEffect(() => {
    if (!showApiKeyInput && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `你好！我是你的AI英语老师。我们可以用英语聊天！你想聊什么话题？`
      }]);
    }
  }, [showApiKeyInput]);

  // Available models
  const availableModels = [
    { name: 'DeepSeek-V4-Pro', group: 'DeepSeek' },
    { name: 'DeepSeek-V4-Flash', group: 'DeepSeek' },
    { name: 'qwen3.7', group: 'Qwen' },
    { name: 'qwen3.6', group: 'Qwen' },
    { name: 'qwen3.5-plus', group: 'Qwen' },
    { name: 'glm-5.1', group: 'GLM' },
    { name: 'glm-5', group: 'GLM' },
    { name: 'MiniMax-M3', group: 'MiniMax' },
    { name: 'MiniMax-2.7', group: 'MiniMax' },
    { name: 'MiniMax-2.5-highspeed', group: 'MiniMax' },
    { name: 'MiniMax-2.5', group: 'MiniMax' },
    { name: 'kimi-k2.6', group: 'Kimi' },
    { name: 'kimi-k2.5', group: 'Kimi' },
  ];

  const topics = [
    { id: 'daily', name: '日常生活', icon: '🏠' },
    { id: 'food', name: '食物与饮食', icon: '🍎' },
    { id: 'hobbies', name: '兴趣爱好', icon: '🎮' },
    { id: 'travel', name: '旅行', icon: '✈️' },
    { id: 'school', name: '学校生活', icon: '🏫' },
    { id: 'family', name: '家庭', icon: '👨‍👩‍👧‍👦' },
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSaveApiKey = () => {
    if (apiKey.trim()) {
      // API key is no longer used client-side, but we keep the UI for future reference
      setShowApiKeyInput(false);
      // Add welcome message
      setMessages([{
        role: 'assistant',
        content: `你好！我是你的AI英语老师。我们可以用英语聊天！你想聊什么话题？`
      }]);
    }
  };

  const handleStartChat = () => {
    // Configure the service with custom endpoint and model
    if (customEndpoint.trim()) {
      aiConversationService.setBackendUrl(customEndpoint.trim());
    }
    if (customModelName) {
      aiConversationService.setModel(customModelName);
    }
    setShowApiKeyInput(false);
    // Add welcome message
    setMessages([{
      role: 'assistant',
      content: `你好！我是你的AI英语老师。我们可以用英语聊天！你想聊什么话题？`
    }]);
  };

  const handleSendMessage = async () => {
    if (!inputText.trim() || isThinking) return;

    const userMessage = inputText.trim();
    setInputText('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsThinking(true);

    try {
      const context: AIConversationContext = {
        level: selectedLevel,
        topic: selectedTopic,
        previousMessages: messages.map(m => ({
          role: m.role,
          content: m.content
        }))
      };

      const aiResponse = await aiConversationService.sendMessage(userMessage, context);
      setMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
    } catch (error: any) {
      console.error('AI error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `抱歉，我遇到了一个问题：${error.message}`
      }]);
    } finally {
      setIsThinking(false);
    }
  };

  const handleSpeakInput = async () => {
    setIsListening(true);
    try {
      const transcript = await sttService.listenForPhrase();
      setInputText(transcript);
    } catch (error: any) {
      console.error('Speech recognition error:', error);
      // More specific error messages
      if (error.message?.includes('not supported')) {
        alert('您的浏览器不支持语音识别，请使用Chrome浏览器或Safari');
      } else if (error.message?.includes('permission')) {
        alert('请允许麦克风权限');
      } else {
        alert(error.message || '语音识别失败，请重试');
      }
    } finally {
      setIsListening(false);
    }
  };

  const handleSpeakMessage = async (text: string) => {
    try {
      await ttsService.speakNormal(text);
    } catch (error) {
      console.error('TTS error:', error);
    }
  };

  // Initial setup screen
  if (showApiKeyInput) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 shadow-xl max-w-md w-full">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">🤖</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">AI 智能对话</h1>
            <p className="text-gray-600 text-sm">
              与AI进行英语对话练习，提升口语表达能力
            </p>
          </div>

          <div className="space-y-4">
            {/* API Endpoint */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Endpoint（你的中转地址）
              </label>
              <input
                type="text"
                value={customEndpoint}
                onChange={(e) => setCustomEndpoint(e.target.value)}
                placeholder="https://your-proxy.com/v1/chat/completions"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* API Key */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Key
              </label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="输入你的 API Key"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* Model Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                选择模型
              </label>
              <select
                value={customModelName}
                onChange={(e) => setCustomModelName(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary bg-white"
              >
                {Object.entries(
                  availableModels.reduce((acc, model) => {
                    if (!acc[model.group]) acc[model.group] = [];
                    acc[model.group].push(model);
                    return acc;
                  }, {} as Record<string, typeof availableModels>)
                ).map(([group, models]) => (
                  <optgroup key={group} label={group}>
                    {models.map((model) => (
                      <option key={model.name} value={model.name}>
                        {model.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                选择你的英语水平
              </label>
              <div className="grid grid-cols-3 gap-2">
                {['beginner', 'intermediate', 'advanced'].map((level) => (
                  <button
                    key={level}
                    onClick={() => setSelectedLevel(level as any)}
                    className={`py-2 rounded-lg font-medium text-sm ${
                      selectedLevel === level
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {level === 'beginner' ? '初级' : level === 'intermediate' ? '中级' : '高级'}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handleStartChat}
              disabled={!customEndpoint.trim() || !apiKey.trim()}
              className={`w-full py-3 rounded-xl font-bold text-white ${
                customEndpoint.trim() && apiKey.trim()
                  ? 'bg-gradient-to-r from-primary to-purple-500 hover:opacity-90'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              开始对话
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 overflow-hidden rounded-2xl" style={{ height: 'calc(100vh - 180px)' }}>
      {/* Header with topic selection */}
      <div className="bg-white border-b px-4 py-3 flex-shrink-0 shadow-sm rounded-t-2xl">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-br from-primary to-purple-500 rounded-full flex items-center justify-center text-white text-xl">
            🤖
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-gray-800">AI 英语老师</h2>
            <p className="text-xs text-gray-500">
              {selectedLevel === 'beginner' ? '初级' : selectedLevel === 'intermediate' ? '中级' : '高级'}水平
            </p>
          </div>
          <button
            onClick={() => setShowApiKeyInput(true)}
            className="text-gray-500 hover:text-gray-700 text-xl px-2"
            title="设置"
          >
            ⚙️
          </button>
        </div>

        {/* Topic buttons */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => setSelectedTopic('')}
            className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
              !selectedTopic
                ? 'bg-primary text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            💬 自由聊天
          </button>
          {topics.map((topic) => (
            <button
              key={topic.id}
              onClick={() => setSelectedTopic(topic.name)}
              className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                selectedTopic === topic.name
                  ? 'bg-primary text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {topic.icon} {topic.name}
            </button>
          ))}
        </div>
      </div>

      {/* Messages - Scrollable area */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50 overscroll-contain">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-3 py-2 ${
                message.role === 'user'
                  ? 'bg-primary text-white'
                  : 'bg-white shadow-soft'
              }`}
            >
              <div className="flex items-start gap-2">
                <p className={`text-sm ${message.role === 'user' ? 'text-white' : 'text-gray-800'}`}>
                  {message.content}
                </p>
                {message.role === 'assistant' && (
                  <button
                    onClick={() => handleSpeakMessage(message.content)}
                    className="text-gray-400 hover:text-primary flex-shrink-0 text-xs"
                  >
                    🔊
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {isThinking && (
          <div className="flex justify-start">
            <div className="bg-white rounded-2xl px-3 py-2 shadow-soft">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area - Fixed at bottom */}
      <div className="bg-white border-t px-4 py-3 flex-shrink-0 shadow-lg">
        <div className="flex items-center gap-3">
          <button
            onClick={handleSpeakInput}
            disabled={isListening}
            className={`p-3 rounded-xl transition-all ${
              isListening
                ? 'bg-red-500 text-white animate-pulse scale-110 shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 active:scale-95'
            }`}
            title={isListening ? '正在录音...' : '点击开始语音输入'}
          >
            {isListening ? '🎤' : '🎙️'}
          </button>

          {isListening && (
            <span className="text-sm text-red-500 animate-pulse font-medium">录音中...</span>
          )}

          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder="输入消息或点击麦克风..."
            className="flex-1 px-4 py-2.5 text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            disabled={isThinking}
          />

          <button
            onClick={handleSendMessage}
            disabled={!inputText.trim() || isThinking}
            className={`p-3 rounded-xl transition-all ${
              inputText.trim() && !isThinking
                ? 'bg-primary text-white hover:opacity-90 shadow-md active:scale-95'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            title="发送消息"
          >
            📤
          </button>
        </div>

        {/* Hint text */}
        <p className="text-xs text-gray-400 mt-2 text-center">
          按 Enter 发送 · 点击麦克风语音输入 · 点击喇叭播放回复
        </p>
      </div>
    </div>
  );
}
